import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Decimal from 'decimal.js'
import { CheckCircle2, CircleAlert, KeyRound, RefreshCw, TriangleAlert, Wrench } from 'lucide-react'
import { Banner, Button, Card, Skeleton, StatusBadge, TextField } from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  reconcileAcquirerWallets,
  reconcileAcquirerWebhook,
  testAcquirer,
  updateAcquirer,
  updateAcquirerStatus,
  useAdminAcquirers,
} from '../api/admin.api'
import type { AcquirerAccount, AcquirerValidationCheck } from '../api/admin.api'
import * as s from './AdminAcquirersScreen.css'

const validationCheckCopy: Record<
  AcquirerAccount['validationReport'][number]['key'],
  Record<AcquirerAccount['validationReport'][number]['status'], string>
> = {
  AUTHENTICATION: {
    PASSED: 'Autenticação válida.',
    WARNING: 'A autenticação da conta requer atenção.',
    FAILED: 'Não foi possível validar a autenticação da conta.',
  },
  CREDENTIAL_PAIR: {
    PASSED: 'Credenciais da conta válidas.',
    WARNING: 'As credenciais da conta requerem atenção.',
    FAILED: 'Não foi possível validar as credenciais da conta.',
  },
  ACCOUNT: {
    PASSED: 'Conta identificada corretamente.',
    WARNING: 'A conta requer atenção operacional.',
    FAILED: 'A conta não está pronta para operar.',
  },
  CHARGES: {
    PASSED: 'Cobranças habilitadas.',
    WARNING: 'As cobranças requerem atenção.',
    FAILED: 'As cobranças não estão habilitadas.',
  },
  APPLE_PAY: {
    PASSED: 'Apple Pay habilitado e ativo.',
    WARNING: 'Apple Pay requer atenção.',
    FAILED: 'Apple Pay requer ajuste.',
  },
  GOOGLE_PAY: {
    PASSED: 'Google Pay habilitado e ativo.',
    WARNING: 'Google Pay requer atenção.',
    FAILED: 'Google Pay requer ajuste.',
  },
  PAYOUTS: {
    PASSED: 'Pagamentos habilitados.',
    WARNING: 'Os pagamentos requerem atenção.',
    FAILED: 'Os pagamentos não estão habilitados.',
  },
  WEBHOOK: {
    PASSED: 'Webhook obrigatório configurado.',
    WARNING: 'A configuração do webhook requer atenção.',
    FAILED: 'O webhook obrigatório não está configurado.',
  },
}

function validationCheckMessage(check: AcquirerAccount['validationReport'][number]) {
  return validationCheckCopy[check.key][check.status]
}

const EMPTY_ACCOUNTS: AcquirerAccount[] = []

export function AdminAcquirersScreen() {
  const session = useSession((state) => state.user)
  const canRead = hasAdminPermission(session, 'ACQUIRER_READ')
  const canManage = hasAdminPermission(session, 'ACQUIRER_MANAGE')
  const queryClient = useQueryClient()
  const acquirers = useAdminAcquirers(canRead)
  const accounts = acquirers.data?.items ?? EMPTY_ACCOUNTS
  const automaticRefreshStarted = useRef(false)
  const refreshAll = useMutation({
    mutationFn: async (accountIds: string[]) => {
      const results = await Promise.allSettled(
        accountIds.map((accountId) => testAcquirer(accountId)),
      )
      return {
        accounts: results.flatMap((result) =>
          result.status === 'fulfilled' ? [result.value.account] : [],
        ),
        failedCount: results.filter((result) => result.status === 'rejected').length,
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-acquirers'] })
    },
  })

  useEffect(() => {
    if (
      automaticRefreshStarted.current ||
      !canManage ||
      !acquirers.isSuccess ||
      accounts.length === 0
    ) {
      return
    }
    automaticRefreshStarted.current = true
    refreshAll.mutate(
      accounts.filter((account) => account.credentialsConfigured).map((account) => account.id),
    )
  }, [accounts, acquirers.isSuccess, canManage, refreshAll])

  const healthy = accounts.filter((account) => account.healthStatus === 'HEALTHY').length
  const active = accounts.filter(
    (account) => account.globalPercentage === 100 && account.acceptsNewTransactions,
  ).length
  const available = aggregateAvailableBalances(accounts)

  return (
    <section className={s.page} data-testid="acquirers-screen">
      <div className={s.header}>
        <div>
          <h1 className={s.title}>Adquirentes</h1>
          <p className={s.subtitle}>
            Consulte as contas Stripe e valide a prontidão da conta configurada para novas
            cobranças.
          </p>
        </div>
        {canRead ? (
          <Button
            variant="secondary"
            leftIcon={<RefreshCw size={16} />}
            disabled={acquirers.isFetching || refreshAll.isPending}
            onClick={() => {
              void acquirers.refetch().then(({ data }) => {
                const accountIds = (data?.items ?? [])
                  .filter((account) => account.credentialsConfigured)
                  .map((account) => account.id)
                if (canManage && accountIds.length > 0) {
                  refreshAll.mutate(accountIds)
                }
              })
            }}
          >
            {refreshAll.isPending
              ? 'Atualizando saldos...'
              : acquirers.isFetching
                ? 'Recarregando...'
                : 'Recarregar dados'}
          </Button>
        ) : null}
      </div>

      <Banner tone="info" title="Conta de cobrança configurada">
        Novas cobranças usam uma única conta definida no ambiente. As outras contas permanecem
        disponíveis para consultar e conciliar operações antigas.
      </Banner>

      <Banner tone="info" title="Como ler estes saldos">
        Esta é uma leitura direta da Stripe, separada por conta e moeda. Disponível já foi liberado
        pela Stripe; em liquidação ainda aguarda o prazo da adquirente. Uma moeda aparece quando a
        Stripe retorna saldo nela. Os valores não são somados nem convertidos entre moedas. O
        snapshot cambial da VendePay registra vendas e repasses internos, mas não altera este saldo
        externo.
      </Banner>

      <div className={s.summaryGrid}>
        <Card>
          <div className={s.summaryCard}>
            <span className={s.eyebrow}>Saúde</span>
            <strong className={s.summaryValue}>
              {acquirers.isPending ? 'Não informado' : `${healthy}/${accounts.length}`}
            </strong>
            <span className={s.muted}>contas saudáveis</span>
          </div>
        </Card>
        <Card>
          <div className={s.summaryCard}>
            <span className={s.eyebrow}>Recebendo tráfego</span>
            <strong className={s.summaryValue}>
              {acquirers.isPending ? 'Não informado' : active}
            </strong>
            <span className={s.muted}>conta configurada e ativa</span>
          </div>
        </Card>
        <Card>
          <div className={s.summaryCard}>
            <span className={s.eyebrow}>Disponível na Stripe</span>
            <strong className={s.summaryValue}>
              {acquirers.isPending ? 'Não informado' : available || 'Não informado'}
            </strong>
            <span className={s.muted}>somado entre contas, sem misturar moedas</span>
          </div>
        </Card>
      </div>

      {!canRead ? (
        <Banner tone="warning" title="Acesso restrito">
          Sua conta não possui a permissão para consultar adquirentes.
        </Banner>
      ) : null}
      {acquirers.isError ? (
        <Banner tone="danger" title="Não foi possível carregar as contas">
          {
            getUserFacingError(acquirers.error, {
              fallback: 'Tente novamente em alguns instantes.',
            }).description
          }
        </Banner>
      ) : null}
      {(refreshAll.data?.failedCount ?? 0) > 0 ? (
        <Banner tone="warning" title="Alguns saldos não puderam ser atualizados">
          {refreshAll.data?.failedCount} conta(s) mantiveram o último diagnóstico disponível.
        </Banner>
      ) : null}
      {canRead && acquirers.isPending ? <Skeleton height={320} /> : null}

      {canRead && !acquirers.isPending ? (
        <AccountsPanel accounts={accounts} canManage={canManage} />
      ) : null}
    </section>
  )
}

function AccountsPanel({
  accounts,
  canManage,
}: {
  accounts: AcquirerAccount[]
  canManage: boolean
}) {
  return (
    <div className={s.panel}>
      <div className={s.panelHeader}>
        <div>
          <h2 className={s.panelTitle}>Contas cadastradas</h2>
          <p className={s.panelCopy}>
            As credenciais são gerenciadas pela equipe técnica no ambiente. Consulte os saldos,
            valide as integrações e acompanhe o estado operacional de cada conta.
          </p>
        </div>
      </div>
      <div className={s.accountGrid}>
        {accounts.map((account) => (
          <AccountCard key={account.id} account={account} canManage={canManage} />
        ))}
      </div>
    </div>
  )
}

function AccountCard({ account, canManage }: { account: AcquirerAccount; canManage: boolean }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [confirmingWebhookRepair, setConfirmingWebhookRepair] = useState(false)
  const [confirmingWalletRepair, setConfirmingWalletRepair] = useState(false)
  const test = useMutation({
    mutationFn: () => testAcquirer(account.id),
    onSuccess: ({ account: updated }) => {
      setConfirmingWebhookRepair(false)
      toast.success(
        updated.healthStatus === 'HEALTHY'
          ? updated.status === 'ACTIVE'
            ? 'Teste concluído: conta saudável e pronta para receber tráfego.'
            : 'Teste concluído: conta saudável. Ative-a explicitamente para receber tráfego.'
          : 'Teste concluído com pendências.',
      )
      void queryClient.invalidateQueries({ queryKey: ['admin-acquirers'] })
    },
    onError: (error) =>
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível testar a conta',
          fallback: 'Solicite à equipe técnica a revisão das credenciais do ambiente.',
        }),
      ),
  })
  const status = useMutation({
    mutationFn: (next: 'ACTIVE' | 'PAUSED' | 'DISABLED') => updateAcquirerStatus(account.id, next),
    onSuccess: () => {
      toast.success('Estado operacional atualizado.')
      void queryClient.invalidateQueries({ queryKey: ['admin-acquirers'] })
    },
    onError: (error) =>
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível atualizar a conta',
          fallback: 'Revise o estado operacional e tente novamente.',
        }),
      ),
  })
  const reconcileWebhook = useMutation({
    mutationFn: () => reconcileAcquirerWebhook(account.id),
    onSuccess: ({ account: updated }) => {
      toast.success(
        updated.healthStatus === 'HEALTHY'
          ? 'Webhook corrigido e conta saudável.'
          : 'Webhook reconciliado, mas a conta ainda possui pendências.',
      )
      void queryClient.invalidateQueries({ queryKey: ['admin-acquirers'] })
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : 'Não foi possível corrigir o webhook.'),
  })
  const reconcileWallets = useMutation({
    mutationFn: () => reconcileAcquirerWallets(account.id),
    onSuccess: ({ account: updated }) => {
      setConfirmingWalletRepair(false)
      const walletChecks = updated.validationReport.filter(isWalletCheck)
      toast.success(
        walletChecks.length === 2 && walletChecks.every((check) => check.status === 'PASSED')
          ? 'Apple Pay e Google Pay revisados e ativos.'
          : 'Ajustes aplicados, mas a Stripe ainda sinaliza pendências nas carteiras.',
      )
      void queryClient.invalidateQueries({ queryKey: ['admin-acquirers'] })
    },
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : 'Não foi possível revisar as carteiras.',
      ),
  })
  const healthPresentation = healthStatus(account.healthStatus)
  const failedWebhookCheck = account.validationReport.find(
    (check) => check.key === 'WEBHOOK' && check.status === 'FAILED',
  )
  const balancesByCurrency = groupBalancesByCurrency(account.balances)
  const walletChecks = account.validationReport.filter(isWalletCheck)
  const walletsReady = walletChecksPassed(walletChecks)
  const operationalChecks = account.validationReport.filter((check) => !isWalletCheck(check))
  const hasNegativeAvailable = balancesByCurrency.some(
    (balance) => Number(balance.available ?? '0') < 0,
  )
  return (
    <Card>
      <article className={s.accountCard}>
        <div className={s.accountHeader}>
          <div className={s.accountHeading}>
            <h3 className={s.accountName}>
              <KeyRound size={18} />
              {account.name}
              <span className={s.modePill}>{stripeModeLabel(account)}</span>
            </h3>
            <div className={s.accountMeta}>
              <span>ID da conta: {account.providerAccountId}</span>
              <span>{account.publishableKey}</span>
              <span>Estado operacional: {operationalStatusLabel(account.status)}</span>
              <span>
                {account.globalPercentage === 100
                  ? 'Conta de cobrança configurada'
                  : 'Conta histórica'}
              </span>
            </div>
          </div>
          <StatusBadge tone={healthPresentation.tone} label={healthPresentation.label} />
        </div>

        <section className={s.balanceSection} aria-label={`Saldo Stripe de ${account.name}`}>
          <div className={s.balanceHeader}>
            <div>
              <h4 className={s.balanceTitle}>Saldo na Stripe</h4>
              <p className={s.balanceHelp}>
                Leitura externa; não é o saldo gerencial nem o snapshot cambial da VendePay.
              </p>
            </div>
            <span className={s.updatedAt}>
              {account.lastHealthCheckedAt
                ? `Atualizado ${formatDateTime(account.lastHealthCheckedAt)}`
                : 'Ainda não consultado'}
            </span>
          </div>
          {balancesByCurrency.length > 0 ? (
            <div className={s.currencyBalanceList}>
              {balancesByCurrency.map((balance) => {
                const negative = Number(balance.available ?? '0') < 0
                return (
                  <div key={balance.currency} className={s.currencyBalanceRow}>
                    <span className={s.currencyCode}>{balance.currency}</span>
                    <div className={`${s.balanceItem} ${negative ? s.negativeBalanceItem : ''}`}>
                      <span className={s.eyebrow}>
                        {negative ? 'Déficit disponível' : 'Disponível agora'}
                      </span>
                      <span className={s.balanceValue}>
                        {formatMoney(balance.available ?? '0', balance.currency)}
                      </span>
                      <span className={s.balanceCaption}>
                        {negative
                          ? 'A Stripe compensará este valor com entradas futuras.'
                          : 'Liberado pela Stripe.'}
                      </span>
                    </div>
                    <div className={s.balanceItem}>
                      <span className={s.eyebrow}>Em liquidação</span>
                      <span className={s.balanceValue}>
                        {formatMoney(balance.pending ?? '0', balance.currency)}
                      </span>
                      <span className={s.balanceCaption}>Ainda não liberado pela Stripe.</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={s.balanceItem}>
              <span className={s.eyebrow}>Saldo</span>
              <span className={s.muted}>Atualize o diagnóstico para consultar a Stripe.</span>
            </div>
          )}
          {hasNegativeAvailable ? (
            <div className={s.negativeBalanceNotice}>
              <CircleAlert size={16} />
              <span>
                Saldo negativo costuma vir de taxas, estornos ou disputas. Este painel apenas lê o
                valor informado pela Stripe e não cria movimentações financeiras.
              </span>
            </div>
          ) : null}
        </section>

        <section className={s.walletSection} aria-label={`Carteiras digitais de ${account.name}`}>
          <div className={s.balanceHeader}>
            <div>
              <h4 className={s.balanceTitle}>Carteiras digitais</h4>
              <p className={s.balanceHelp}>
                Status da conta Stripe e do domínio público usado pelo checkout.
              </p>
            </div>
            {canManage && !confirmingWalletRepair ? (
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Wrench size={14} />}
                disabled={reconcileWallets.isPending}
                onClick={() => setConfirmingWalletRepair(true)}
              >
                Revisar e corrigir carteiras
              </Button>
            ) : null}
          </div>
          <div className={s.walletGrid}>
            <WalletStatus
              label="Apple Pay"
              check={walletChecks.find((check) => check.key === 'APPLE_PAY')}
            />
            <WalletStatus
              label="Google Pay"
              check={walletChecks.find((check) => check.key === 'GOOGLE_PAY')}
            />
          </div>
          {canManage && confirmingWalletRepair ? (
            <div className={s.webhookRepairPanel}>
              <Banner tone="warning" title="Confirmar revisão das carteiras">
                A VendePay revisará a configuração padrão da Stripe, habilitará Apple Pay e Google
                Pay e criará, reativará ou validará o domínio do checkout quando necessário.
              </Banner>
              <div className={s.actionRow}>
                <Button
                  size="sm"
                  leftIcon={<Wrench size={14} />}
                  disabled={reconcileWallets.isPending}
                  onClick={() => reconcileWallets.mutate()}
                >
                  {reconcileWallets.isPending ? 'Revisando...' : 'Confirmar revisão'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={reconcileWallets.isPending}
                  onClick={() => setConfirmingWalletRepair(false)}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : null}
        </section>

        <ul className={s.checkList}>
          {operationalChecks.length > 0 ? (
            operationalChecks.map((check) => {
              const Icon =
                check.status === 'PASSED'
                  ? CheckCircle2
                  : check.status === 'WARNING'
                    ? TriangleAlert
                    : CircleAlert
              const className =
                check.status === 'PASSED'
                  ? s.checkPassed
                  : check.status === 'WARNING'
                    ? s.checkWarning
                    : s.checkFailed
              return (
                <li key={check.key} className={s.checkItem}>
                  <Icon className={className} size={16} />
                  <span>{validationCheckMessage(check)}</span>
                </li>
              )
            })
          ) : (
            <li className={s.checkItem}>
              <CircleAlert className={s.checkWarning} size={16} />
              <span>Aguardando primeiro teste completo.</span>
            </li>
          )}
        </ul>

        {failedWebhookCheck ? (
          <div className={s.webhookRepairPanel}>
            <Banner
              tone={account.webhookSecretConfigured ? 'warning' : 'danger'}
              title={
                !account.webhookSecretConfigured
                  ? 'Correção manual necessária'
                  : confirmingWebhookRepair
                    ? 'Confirmar correção do webhook'
                    : 'Webhook Stripe precisa de correção'
              }
            >
              {account.webhookSecretConfigured && confirmingWebhookRepair
                ? `A VendePay atualizará os eventos obrigatórios no endpoint Stripe de ${account.name} e manterá o signing secret atual.`
                : account.webhookSecretConfigured
                  ? 'O diagnóstico encontrou eventos obrigatórios ausentes. Revise a conta e execute a correção explícita.'
                  : 'O signing secret desta conta não está armazenado. Reconcilie o endpoint diretamente na Stripe antes de testar novamente.'}
            </Banner>
            {canManage && account.webhookSecretConfigured ? (
              confirmingWebhookRepair ? (
                <div className={s.actionRow}>
                  <Button
                    size="sm"
                    leftIcon={<Wrench size={14} />}
                    disabled={reconcileWebhook.isPending}
                    onClick={() => reconcileWebhook.mutate()}
                  >
                    {reconcileWebhook.isPending ? 'Corrigindo...' : 'Confirmar correção'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={reconcileWebhook.isPending}
                    onClick={() => setConfirmingWebhookRepair(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Wrench size={14} />}
                  onClick={() => setConfirmingWebhookRepair(true)}
                >
                  Corrigir webhook
                </Button>
              )
            ) : null}
          </div>
        ) : null}

        {editing ? <EditAccountForm account={account} onClose={() => setEditing(false)} /> : null}

        <div className={s.actionRow}>
          {canManage ? (
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<RefreshCw size={14} />}
              disabled={test.isPending}
              onClick={() => test.mutate()}
            >
              {test.isPending ? 'Atualizando...' : 'Atualizar saldo e diagnóstico'}
            </Button>
          ) : null}
          {canManage ? (
            <Button size="sm" variant="secondary" onClick={() => setEditing((value) => !value)}>
              {editing ? 'Fechar edição' : 'Editar nome'}
            </Button>
          ) : null}
          {canManage && account.status === 'ACTIVE' ? (
            <Button size="sm" variant="secondary" onClick={() => status.mutate('PAUSED')}>
              Pausar
            </Button>
          ) : null}
          {canManage && account.status !== 'ACTIVE' && account.healthStatus === 'HEALTHY' ? (
            <>
              <Button
                size="sm"
                disabled={!walletsReady}
                title={
                  walletsReady
                    ? undefined
                    : 'Revise e corrija Apple Pay e Google Pay antes de ativar esta conta.'
                }
                onClick={() => status.mutate('ACTIVE')}
              >
                Ativar
              </Button>
              {!walletsReady ? (
                <span className={s.activationHint}>
                  <TriangleAlert size={14} />
                  Revise e corrija Apple Pay e Google Pay antes de ativar esta conta.
                </span>
              ) : null}
            </>
          ) : null}
          {canManage && account.status !== 'DISABLED' ? (
            <Button size="sm" variant="danger" onClick={() => status.mutate('DISABLED')}>
              Desativar
            </Button>
          ) : null}
        </div>
      </article>
    </Card>
  )
}

function EditAccountForm({ account, onClose }: { account: AcquirerAccount; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: account.name,
  })
  const update = useMutation({
    mutationFn: () => updateAcquirer(account.id, { name: form.name }),
    onSuccess: () => {
      toast.success('Nome da conta atualizado.')
      void queryClient.invalidateQueries({ queryKey: ['admin-acquirers'] })
      onClose()
    },
    onError: (error) =>
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível atualizar a conta',
          fallback: 'Revise os dados e tente novamente.',
        }),
      ),
  })
  return (
    <form
      className={s.panel}
      onSubmit={(event) => {
        event.preventDefault()
        update.mutate()
      }}
    >
      <TextField
        label="Nome para identificação"
        value={form.name}
        onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))}
      />
      <div className={s.actionRow}>
        <Button type="submit" disabled={form.name.trim().length < 2 || update.isPending}>
          {update.isPending ? 'Salvando...' : 'Salvar alterações'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </form>
  )
}

function WalletStatus({
  label,
  check,
}: {
  label: 'Apple Pay' | 'Google Pay'
  check: AcquirerValidationCheck | undefined
}) {
  const status = walletStatus(check)
  return (
    <div className={s.walletCard}>
      <div className={s.walletHeader}>
        <strong className={s.walletName}>{label}</strong>
        <StatusBadge tone={status.tone} label={status.label} size="sm" />
      </div>
      <span className={s.walletMessage}>
        {check?.message ?? 'Execute a revisão para consultar a configuração desta conta.'}
      </span>
    </div>
  )
}

function isWalletCheck(check: AcquirerValidationCheck): boolean {
  return check.key === 'APPLE_PAY' || check.key === 'GOOGLE_PAY'
}

function walletChecksPassed(checks: AcquirerValidationCheck[]): boolean {
  return ['APPLE_PAY', 'GOOGLE_PAY'].every((key) =>
    checks.some((check) => check.key === key && check.status === 'PASSED'),
  )
}

function walletStatus(check: AcquirerValidationCheck | undefined) {
  if (check?.status === 'PASSED') return { label: 'Ativo', tone: 'success' as const }
  if (check?.status === 'FAILED') return { label: 'Precisa de ajuste', tone: 'closed' as const }
  if (check?.status === 'WARNING') return { label: 'Ação manual', tone: 'awaiting' as const }
  return { label: 'Não verificado', tone: 'neutral' as const }
}

function operationalStatusLabel(status: AcquirerAccount['status']): string {
  switch (status) {
    case 'ACTIVE':
      return 'Ativa'
    case 'PAUSED':
      return 'Pausada'
    case 'DISABLED':
      return 'Desativada'
    case 'DEGRADED':
      return 'Com pendências'
    case 'DRAFT':
      return 'Aguardando ativação'
  }
}

function healthStatus(status: AcquirerAccount['healthStatus']) {
  if (status === 'HEALTHY') return { label: 'Saudável', tone: 'success' as const }
  if (status === 'UNAVAILABLE') return { label: 'Indisponível', tone: 'closed' as const }
  if (status === 'DEGRADED') return { label: 'Com pendências', tone: 'awaiting' as const }
  return { label: 'Aguardando teste', tone: 'neutral' as const }
}

function aggregateAvailableBalances(accounts: AcquirerAccount[]): string {
  const totals = new Map<string, Decimal>()
  for (const account of accounts) {
    for (const balance of account.balances) {
      if (balance.kind !== 'AVAILABLE') continue
      const current = totals.get(balance.currency) ?? new Decimal(0)
      totals.set(balance.currency, current.plus(balance.amount))
    }
  }
  return [...totals.entries()]
    .map(([currency, amount]) => formatMoney(amount.toString(), currency))
    .join(' · ')
}

function formatMoney(amount: string, currency: string): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(Number(amount))
}

function groupBalancesByCurrency(balances: AcquirerAccount['balances']) {
  const grouped = new Map<string, { currency: string; available?: string; pending?: string }>()
  for (const balance of balances) {
    const current = grouped.get(balance.currency) ?? { currency: balance.currency }
    grouped.set(balance.currency, {
      ...current,
      ...(balance.kind === 'AVAILABLE'
        ? { available: balance.amount }
        : { pending: balance.amount }),
    })
  }
  return [...grouped.values()].sort((left, right) => left.currency.localeCompare(right.currency))
}

function stripeModeLabel(account: AcquirerAccount): string {
  const mode =
    account.mode ??
    (account.publishableKey.startsWith('pk_test_')
      ? 'TEST'
      : account.publishableKey.startsWith('pk_live_')
        ? 'LIVE'
        : 'UNKNOWN')
  if (mode === 'TEST') return 'Stripe teste'
  if (mode === 'LIVE') return 'Stripe produção'
  return 'Ambiente não identificado'
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}
